import { Component, Inject, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Utils } from '../../../../../shared/utils';
import { NotificationService } from '../../../../shared/services/notification.service';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';
import { EMPTY_GUID, EntityFormOption, optionId, serverErrorMessage } from '../../../../finance/entity-form/entity-form.utils';
import {
  FINANCIAL_PAY_METHOD_OPTIONS,
  FINANCIAL_PAY_METHODS,
  FinancialInvestmentDeposit,
  FinancialInvestmentPayDialogData,
  roundMoney,
} from '../../models/financial-investment.model';
import { FinancialInvestmentEntityService } from '../../services/financial-investment-entity.service';
import { FinancialInvestmentLookupsFacade, FinancialPayLookupKind } from '../../services/financial-investment-lookups.facade';

type PayErrors = Partial<Record<'typePay' | 'depositDate' | 'amount' | 'bankAccount' | 'creditCard' | 'cash' | 'partner' | 'creditPolicy' | 'commissions', string>>;

const KIND_CONTROLS: Record<FinancialPayLookupKind, string> = {
  bankAccounts: 'bankAccount',
  creditCards: 'creditCard',
  cash: 'cash',
  partners: 'partner',
  creditPolicies: 'creditPolicy',
};

@Component({
  selector: 'app-financial-investment-pay-dialog',
  templateUrl: './financial-investment-pay-dialog.component.html',
  styleUrls: ['./financial-investment-pay-dialog.component.scss'],
  standalone: false,
})
export class FinancialInvestmentPayDialogComponent implements OnInit {
  readonly methods = FINANCIAL_PAY_METHODS;
  readonly methodConfig = {
    label: 'Tipo de pago',
    placeholder: 'Seleccionar tipo de pago…',
    required: true,
    options: FINANCIAL_PAY_METHOD_OPTIONS.map(method => ({ id: method, display: method })),
    strict: true,
    onChange: () => this.onTypePayChange(),
  };

  form: UntypedFormGroup;

  ready = false;
  loadError = false;
  saving = false;
  savingMessage = '';
  submitted = false;
  saveError = '';
  changed = false;

  deposits: FinancialInvestmentDeposit[] = [];
  editing: FinancialInvestmentDeposit | null = null;

  options: Record<FinancialPayLookupKind, EntityFormOption[]> = {
    bankAccounts: [],
    creditCards: [],
    cash: [],
    partners: [],
    creditPolicies: [],
  };
  loadingOptions: Partial<Record<FinancialPayLookupKind, boolean>> = {};

  configs: Record<FinancialPayLookupKind, any> = {
    bankAccounts: { label: 'Cuenta bancaria', placeholder: 'Seleccionar cuenta…', required: true, autoSelectSingle: true, strict: true, options: [] },
    creditCards: { label: 'Tarjeta de crédito', placeholder: 'Seleccionar tarjeta…', required: true, autoSelectSingle: true, strict: true, options: [] },
    cash: { label: 'Caja / Efectivo', placeholder: 'Seleccionar caja…', required: true, autoSelectSingle: true, strict: true, options: [] },
    partners: { label: 'Socio', placeholder: 'Seleccionar socio…', required: true, autoSelectSingle: true, strict: true, options: [] },
    creditPolicies: { label: 'Póliza de crédito', placeholder: 'Seleccionar póliza…', required: true, autoSelectSingle: true, strict: true, options: [] },
  };

  private readonly missingLabels: Record<FinancialPayLookupKind, string> = {
    bankAccounts: 'Cuenta bancaria',
    creditCards: 'Tarjeta de crédito',
    cash: 'Caja',
    partners: 'Socio',
    creditPolicies: 'Póliza de crédito',
  };

  private readonly emptyHints: Record<FinancialPayLookupKind, string> = {
    bankAccounts: 'No tienes cuentas bancarias dadas de alta.',
    creditCards: 'No tienes tarjetas de crédito dadas de alta.',
    cash: 'No tienes cajas dadas de alta.',
    partners: 'No tienes socios dados de alta.',
    creditPolicies: 'No tienes pólizas de crédito dadas de alta.',
  };

  private requestedEditDone = false;
  private readonly optionRequests = new Map<FinancialPayLookupKind, Promise<void>>();
  private readonly pendingSelection = new Map<FinancialPayLookupKind, { id: any; label: string }>();
  private readonly loadedKinds = new Set<FinancialPayLookupKind>();

  static open(dialog: MatDialog, data: FinancialInvestmentPayDialogData): Observable<boolean> {
    return dialog.open<FinancialInvestmentPayDialogComponent, FinancialInvestmentPayDialogData, boolean>(FinancialInvestmentPayDialogComponent, {
      width: '700px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    }).afterClosed().pipe(map(result => result === true));
  }

  constructor(
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<FinancialInvestmentPayDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: FinancialInvestmentPayDialogData,
    private entity: FinancialInvestmentEntityService,
    private lookups: FinancialInvestmentLookupsFacade,
    private notify: NotificationService,
    private confirm: ConfirmService,
  ) {
    this.form = this.fb.group({
      typePay: [null],
      depositDate: [new Date()],
      amount: [null],
      commissions: [null],
      bankAccount: [null],
      creditCard: [null],
      cash: [null],
      partner: [null],
      creditPolicy: [null],
    });
  }

  get subtitle(): string {
    const investment = this.data.investment;
    return [investment.investmentDescription, investment.accountNumber].filter(Boolean).join(' · ');
  }

  get typePayControl(): UntypedFormControl {
    return this.form.get('typePay') as UntypedFormControl;
  }

  get depositDateControl(): UntypedFormControl {
    return this.form.get('depositDate') as UntypedFormControl;
  }

  get amountControl(): UntypedFormControl {
    return this.form.get('amount') as UntypedFormControl;
  }

  get commissionsControl(): UntypedFormControl {
    return this.form.get('commissions') as UntypedFormControl;
  }

  get typePay(): string | null {
    return optionId(this.form.get('typePay').value);
  }

  control(kind: FinancialPayLookupKind): UntypedFormControl {
    return this.form.get(KIND_CONTROLS[kind]) as UntypedFormControl;
  }

  selectedId(kind: FinancialPayLookupKind): any {
    return optionId(this.control(kind).value);
  }

  get total(): number {
    return roundMoney(this.data.investment.investmentValue);
  }

  get paidAmount(): number {
    return roundMoney(this.deposits.reduce((acc, d) => acc + (Number(d.amount) || 0), 0));
  }

  get remaining(): number {
    return roundMoney(this.total - this.paidAmount);
  }

  get isPaid(): boolean {
    return this.deposits.length > 0 && this.remaining <= 0;
  }

  get maxAmount(): number {
    return roundMoney(this.remaining + (this.editing ? Number(this.editing.amount) || 0 : 0));
  }

  get showForm(): boolean {
    return !this.isPaid || !!this.editing;
  }

  get errors(): PayErrors {
    const value = this.form.getRawValue();
    const type = this.typePay;
    const errors: PayErrors = {};
    if (!type) errors.typePay = 'Selecciona el tipo de pago';
    if (!value.depositDate) errors.depositDate = 'Indica la fecha de pago';

    const amount = Number(value.amount);
    if (value.amount === null || value.amount === '' || isNaN(amount) || amount <= 0) {
      errors.amount = 'Indica un importe mayor que cero';
    } else if (amount > this.maxAmount + 0.001) {
      errors.amount = `No puede superar el pendiente (${Utils.FormatMoney(this.maxAmount)} €)`;
    }

    if (type === this.methods.transfer && !this.selectedId('bankAccounts')) errors.bankAccount = 'Selecciona la cuenta bancaria';
    if (type === this.methods.creditCard && !this.selectedId('creditCards')) errors.creditCard = 'Selecciona la tarjeta de crédito';
    if (type === this.methods.cash && !this.selectedId('cash')) errors.cash = 'Selecciona la caja';
    if (type === this.methods.partner && !this.selectedId('partners')) errors.partner = 'Selecciona el socio';
    if (type === this.methods.creditPolicy && !this.selectedId('creditPolicies')) errors.creditPolicy = 'Selecciona la póliza de crédito';

    if (type === this.methods.transfer && value.commissions !== null && value.commissions !== ''
      && (isNaN(Number(value.commissions)) || Number(value.commissions) < 0)) {
      errors.commissions = 'Indica una comisión válida';
    }
    return errors;
  }

  get hasErrors(): boolean {
    return Object.keys(this.errors).length > 0;
  }

  ngOnInit(): void {
    this.loadDeposits(true);
  }

  retry(): void {
    this.loadDeposits(true);
  }

  assignRemaining(): void {
    this.amountControl.setValue(this.maxAmount > 0 ? this.maxAmount : null);
  }

  onTypePayChange(): void {
    const kind = this.lookupKind(this.typePay);
    if (kind) this.ensureOptions(kind);
  }

  startEdit(deposit: FinancialInvestmentDeposit): void {
    this.editing = deposit;
    this.submitted = false;
    this.saveError = '';
    this.pendingSelection.clear();

    const type = FINANCIAL_PAY_METHOD_OPTIONS.indexOf(deposit.typePay) !== -1 ? deposit.typePay : null;
    this.form.patchValue({
      typePay: type ? { id: type, display: type } : null,
      depositDate: deposit.depositDate ? new Date(deposit.depositDate) : null,
      amount: Number(deposit.amount) || null,
      commissions: Number(deposit.commissions) || null,
      bankAccount: null,
      creditCard: null,
      cash: null,
      partner: null,
      creditPolicy: null,
    }, { emitEvent: false });

    const info = deposit.bankAccountInfo;
    this.rememberSelection('bankAccounts', this.guidOrNull(deposit.bankAccountsUid), info);
    this.rememberSelection('creditCards', this.guidOrNull(deposit.paymentCardsUid), info);
    this.rememberSelection('cash', deposit.efectivoId || null, info);
    this.rememberSelection('partners', deposit.partnersId || null, info);
    this.rememberSelection('creditPolicies', deposit.polizaCreditoId || null, info);

    this.onTypePayChange();
    this.applyPendingSelections();
  }

  cancelEdit(): void {
    this.editing = null;
    this.resetForm();
  }

  save(): void {
    if (this.saving) return;
    this.submitted = true;
    this.saveError = '';
    if (this.hasErrors) return;

    const editing = this.editing;
    const payload = this.buildPayload();
    const request = editing
      ? this.entity.updateDeposit({ ...payload, uid: editing.uid })
      : this.entity.createDeposit(payload);

    this.saving = true;
    this.savingMessage = editing ? 'Actualizando pago…' : 'Registrando pago…';
    this.dialogRef.disableClose = true;

    request
      .then(() => {
        this.changed = true;
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.notify.ok(editing ? 'Pago actualizado correctamente' : 'Pago registrado correctamente');
        if (!editing || this.data.editUid) {
          this.dialogRef.close(true);
          return;
        }
        this.editing = null;
        this.resetForm();
        this.loadDeposits(false);
      })
      .catch(error => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.saveError = serverErrorMessage(error, editing ? 'Ha ocurrido un error inesperado al actualizar el pago' : 'Ha ocurrido un error inesperado al añadir el pago');
      });
  }

  deleteDeposit(deposit: FinancialInvestmentDeposit): void {
    if (this.saving) return;
    this.confirm.open({
      title: 'Eliminar pago',
      subtitle: this.subtitle || undefined,
      message: 'Se eliminará el pago y su asiento contable. Si estaba conciliado, el movimiento bancario volverá a quedar pendiente. Esta acción no se puede deshacer.',
      tone: 'danger',
      details: depositDetails(deposit),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando pago…',
      errorText: 'No se pudo eliminar el pago',
      action: () => this.entity.deleteDeposit(deposit.uid),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.changed = true;
      this.notify.ok('Pago eliminado correctamente');
      if (this.editing && this.editing.uid === deposit.uid) this.cancelEdit();
      this.loadDeposits(false);
    });
  }

  close(): void {
    if (this.saving) return;
    this.dialogRef.close(this.changed);
  }

  trackByUid(_: number, deposit: FinancialInvestmentDeposit): string {
    return deposit.uid;
  }

  private loadDeposits(initial: boolean): void {
    if (initial) {
      this.ready = false;
      this.loadError = false;
    }
    this.entity.deposits(this.data.investment.uid)
      .then(list => {
        this.deposits = Array.isArray(list) ? list : [];
        if (initial) this.resetForm();
        this.openRequestedEdit();
      })
      .catch(() => {
        if (initial) this.loadError = true;
        else this.notify.error('No se pudieron recargar los pagos');
      })
      .then(() => { this.ready = true; });
  }

  private openRequestedEdit(): void {
    if (!this.data.editUid || this.requestedEditDone) return;
    this.requestedEditDone = true;
    const deposit = this.deposits.find(item => item.uid === this.data.editUid);
    if (deposit) this.startEdit(deposit);
  }

  private lookupKind(typePay: string | null): FinancialPayLookupKind | null {
    switch (typePay) {
      case this.methods.transfer: return 'bankAccounts';
      case this.methods.creditCard: return 'creditCards';
      case this.methods.cash: return 'cash';
      case this.methods.partner: return 'partners';
      case this.methods.creditPolicy: return 'creditPolicies';
      default: return null;
    }
  }

  private ensureOptions(kind: FinancialPayLookupKind): void {
    if (this.optionRequests.has(kind)) return;
    this.loadingOptions[kind] = true;
    this.configs[kind] = { ...this.configs[kind], hint: 'Cargando…' };
    const request = this.lookups.payOptions(kind)
      .then(list => {
        this.options[kind] = list || [];
        this.loadedKinds.add(kind);
        this.configs[kind] = {
          ...this.configs[kind],
          options: this.options[kind],
          hint: this.options[kind].length ? undefined : this.emptyHints[kind],
        };
        this.applyPendingSelections();
      })
      .catch(() => {
        this.optionRequests.delete(kind);
        this.configs[kind] = { ...this.configs[kind], hint: undefined };
        this.notify.error('No se pudieron cargar las opciones del tipo de pago');
      })
      .then(() => { this.loadingOptions[kind] = false; });
    this.optionRequests.set(kind, request);
  }

  private rememberSelection(kind: FinancialPayLookupKind, id: any, label: string): void {
    if (id === null || id === undefined || id === '') return;
    this.pendingSelection.set(kind, { id, label: label || '' });
  }

  private applyPendingSelections(): void {
    this.pendingSelection.forEach((pending, kind) => {
      if (!this.loadedKinds.has(kind)) return;
      const option = this.findOption(this.options[kind], pending.id) || this.keepMissingOption(kind, pending);
      this.control(kind).setValue(option, { emitEvent: false });
      this.pendingSelection.delete(kind);
    });
  }

  private keepMissingOption(kind: FinancialPayLookupKind, pending: { id: any; label: string }): any {
    const text = String(pending.label || '').trim() || this.missingLabels[kind];
    const option = { id: pending.id, display: text + ' (no disponible)', disabled: true };
    this.options[kind] = [...this.options[kind], option];
    this.configs[kind] = { ...this.configs[kind], options: this.options[kind] };
    return option;
  }

  private findOption(options: { id: any }[], id: any): any {
    const key = String(id);
    return options.find(option => String(option.id) === key)
      || options.find(option => String(option.id).toLowerCase() === key.toLowerCase())
      || null;
  }

  private buildPayload(): any {
    const value = this.form.getRawValue();
    const type = this.typePay as string;
    return {
      uid: null,
      investmentsFinancialId: this.data.investment.uid,
      typePay: type,
      depositDate: formatDate(value.depositDate as Date, 'yyyy-MM-dd', 'es-ES'),
      amount: roundMoney(Number(value.amount)),
      commissions: type === this.methods.transfer ? roundMoney(Number(value.commissions) || 0) : 0,
      bankAccountsUid: type === this.methods.transfer && this.selectedId('bankAccounts') ? this.selectedId('bankAccounts') : EMPTY_GUID,
      paymentCardsUid: type === this.methods.creditCard && this.selectedId('creditCards') ? this.selectedId('creditCards') : EMPTY_GUID,
      efectivoId: type === this.methods.cash ? this.selectedId('cash') : null,
      partnersId: type === this.methods.partner ? Number(this.selectedId('partners')) || 0 : 0,
      polizaCreditoId: type === this.methods.creditPolicy ? this.selectedId('creditPolicies') : null,
    };
  }

  private resetForm(): void {
    this.submitted = false;
    this.saveError = '';
    this.pendingSelection.clear();
    this.form.reset({
      typePay: null,
      depositDate: new Date(),
      amount: this.remaining > 0 ? this.remaining : null,
      commissions: null,
      bankAccount: null,
      creditCard: null,
      cash: null,
      partner: null,
      creditPolicy: null,
    }, { emitEvent: false });
  }

  private guidOrNull(value: string | null | undefined): string | null {
    return value && value !== EMPTY_GUID ? value : null;
  }
}

export function depositDetails(deposit: FinancialInvestmentDeposit): ConfirmDialogDetail[] {
  const details: ConfirmDialogDetail[] = [
    { label: 'Importe', value: Utils.FormatMoney(Number(deposit.amount) || 0) + ' €' },
  ];
  if (deposit.depositDate) details.push({ label: 'Fecha', value: formatDate(deposit.depositDate, 'dd/MM/yyyy', 'es-ES') });
  if (deposit.typePay) details.push({ label: 'Forma de pago', value: deposit.typePay + (deposit.bankAccountInfo ? ' · ' + deposit.bankAccountInfo : '') });
  return details;
}
